import React, { useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { Route } from "src/config/routes";
import { observer } from "mobx-react-lite";
import { UI, ScreenSize } from "@wwimmo/ui";
import styles from "src/screens/dashboard/customers/customer/list/CustomerList.module.css";
import copiedStyles from "./CopiedCustomerList.module.css";

interface ICopiedCustomerListItem {
    id: string;
    name: string | null;
    number: string | null;
    copyStatus?: 1 | 2 | 3 | null;
    copyProgress?: number | null;
    copiedBy?: string | null;
}

interface ICopiedCustomerListProps {
    umsCustomers: ICopiedCustomerListItem[];
    totalEntries: number;
    currentPage: number;
    itemsPerPage: number;
    onPageChanged: (currentPage: number) => void;
    onSearch: (searchArguments: {
        searchQuery?: string;
        filterItemValue?: string;
        sortItemValue?: string;
    }) => void;
    searchAppendElement?: React.ReactNode;
}

const FAILED_COPY_PROGRESS_LIMIT = 99;
const COMPLETED_COPY_PROGRESS_LIMIT = 100;
const LIST_HEADER_TOP = 130;

const CopiedCustomerList = (props: ICopiedCustomerListProps) => {
    const { t } = useTranslation();
    const { uiStore, navStore } = useContext(RootStoreContext);
    const { onPageChanged: onPageChangedProp, onSearch: onSearchProp } = props;

    const listIdentifier = "copiedcustomerlist";

    if (!navStore.listMap.has(listIdentifier)) {
        navStore.setListMapValue(listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(listIdentifier);
    const initialSearchQuery = useRef<string | undefined>(listParameters?.searchQuery ?? undefined).current;

    const onRowClick = useCallback(() => {
        navStore.setListMapValue(
            listIdentifier,
            listParameters
                ? { ...listParameters, scrollPosition: window.pageYOffset }
                : { scrollPosition: window.pageYOffset }
        );

        navStore.setBackbutton(Route.customersCopied, t("screens.dashboard.nav.copied_customers"));
    }, [listParameters, navStore, t]);

    const onPageChanged = useCallback(
        (currentPage: number) => {
            navStore.setListMapValue(
                listIdentifier,
                listParameters
                    ? { ...listParameters, scrollPosition: window.pageYOffset, paginationPage: currentPage }
                    : { scrollPosition: window.pageYOffset, paginationPage: currentPage }
            );

            onPageChangedProp(currentPage);
        },
        [listIdentifier, listParameters, navStore, onPageChangedProp]
    );

    const onSearch = useCallback(
        (searchArguments: { searchQuery?: string; filterItemValue?: string; sortItemValue?: string }) => {
            const nextSearchQuery = searchArguments.searchQuery ?? "";

            navStore.setListMapValue(listIdentifier, {
                ...(listParameters ?? {}),
                searchQuery: nextSearchQuery,
                paginationPage: 1,
                scrollPosition: undefined
            });

            onSearchProp(searchArguments);
        },
        [listIdentifier, listParameters, navStore, onSearchProp]
    );

    const getStatusLabel = useCallback((status?: 1 | 2 | 3 | null) => {
        switch (status) {
            case 1:
                return t("screens.dashboard.copy_customer.status.running");
            case 2:
                return t("screens.dashboard.copy_customer.status.completed");
            case 3:
                return t("screens.dashboard.copy_customer.status.failed");
            default:
                return t("screens.dashboard.copy_customer.status.running");
        }
    }, [t]);

    const getStatusFillClassName = useCallback((status?: 1 | 2 | 3 | null) => {
        switch (status) {
            case 1:
                return copiedStyles.statusBarFilledRunning;
            case 2:
                return copiedStyles.statusBarFilledDone;
            case 3:
                return copiedStyles.statusBarFilledFailed;
            default:
                return copiedStyles.statusBarFilledRunning;
        }
    }, []);

    const renderStatusBar = useCallback((status?: 1 | 2 | 3 | null, progress?: number | null) => {
        const normalizedProgress = normalizeProgress(progress, status);
        const filledWidth = `${normalizedProgress}%`;
        const filledClassName =
            normalizedProgress >= 100
                ? `${copiedStyles.statusBarFilled} ${getStatusFillClassName(status)} ${copiedStyles.statusBarFilledComplete}`
                : `${copiedStyles.statusBarFilled} ${getStatusFillClassName(status)}`;

        return (
            <div className={copiedStyles.statusBar}>
                <div className={filledClassName} style={{ width: filledWidth }} />
                {status === 1 && normalizedProgress < 100 ? (
                    <div
                        className={`${copiedStyles.statusBarRemaining} ${copiedStyles.statusBarRemainingAnimated}`}
                        style={{ left: filledWidth, width: `calc(100% - ${filledWidth})` }}
                    />
                ) : null}
            </div>
        );
    }, [getStatusFillClassName]);

    const renderItem = useCallback(
        (customer: ICopiedCustomerListItem, screenSize: ScreenSize) => {
            const progressValue = normalizeProgress(customer.copyProgress, customer.copyStatus);
            const statusLabel = getStatusLabel(customer.copyStatus);

            const listRows =
                screenSize === ScreenSize.DESKTOP
                    ? [
                          <UI.List.Row key="desktop-row">
                              <UI.List.Cell key="name" colspan={1.2} value={customer.name ? customer.name : ""} />
                              <UI.List.Cell key="number" colspan={0.5} value={customer.number ? customer.number : ""} />
                              <UI.List.Cell
                                  key="status"
                                  colspan={0.9}
                                  value={
                                      <div className={copiedStyles.statusContainer}>
                                          <div className={copiedStyles.statusMeta}>
                                              <span className="small">{statusLabel}</span>
                                              <span className={copiedStyles.statusPercent}>{`${progressValue}%`}</span>
                                          </div>
                                          {renderStatusBar(customer.copyStatus, progressValue)}
                                      </div>
                                  }
                              />
                              <UI.List.Cell key="copiedBy" colspan={0.5} value={customer.copiedBy ?? "-"} />
                          </UI.List.Row>
                      ]
                    : [
                          <UI.List.Row key="mobile-row">
                              <UI.List.Cell key="mobile-name" colspan={1.2} value={customer.name ? customer.name : ""} />
                          </UI.List.Row>,
                          <UI.List.Row key="mobile-status-row">
                              <UI.List.Cell
                                  key="mobile-status"
                                  colspan={1.2}
                                  value={
                                      <div className={copiedStyles.statusContainer}>
                                          <div className={copiedStyles.statusMeta}>
                                              <span className="small">{statusLabel}</span>
                                              <span className={copiedStyles.statusPercent}>{`${progressValue}%`}</span>
                                          </div>
                                          {renderStatusBar(customer.copyStatus, progressValue)}
                                      </div>
                                  }
                              />
                          </UI.List.Row>
                      ];

            return (
                <UI.List.Item
                    key={customer.id}
                    screenSize={screenSize}
                    rows={listRows}
                    to={`/customers/${customer.id}`}
                    onClick={onRowClick}
                />
            );
        },
        [getStatusLabel, onRowClick, renderStatusBar]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            const headerRows =
                screenSize === ScreenSize.DESKTOP
                    ? [
                          <UI.List.Row key="row-1">
                              <UI.List.Cell key="name" colspan={1.2} value={t("customer.name")} />
                              <UI.List.Cell key="number" colspan={0.5} value={t("customer.customer_number")} />
                              <UI.List.Cell key="status" colspan={0.9} value={t("screens.customer_details.form.status")} />
                              <UI.List.Cell
                                  key="copiedBy"
                                  colspan={0.5}
                                  value={t("screens.dashboard.copy_customer.copied_by")}
                              />
                          </UI.List.Row>
                      ]
                    : [
                          <UI.List.Row key="row-1">
                              <UI.List.Cell key="mobile-name" colspan={1.2} value={t("customer.name")} />
                          </UI.List.Row>
                      ];

            return <UI.List.Header rows={headerRows} sticky={true} top={LIST_HEADER_TOP} />;
        },
        [t]
    );

    return (
        <>
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={props.umsCustomers}
                renderItem={renderItem}
                renderHeader={renderHeader}
                scrollPosition={listParameters?.scrollPosition ?? undefined}
                itemsPerPage={Math.max(props.umsCustomers.length, 1)}
                initialPage={1}
                onSearch={onSearch}
                searchAppendElement={props.searchAppendElement}
                initialSearchQuery={initialSearchQuery}
            />
            {props.totalEntries > props.itemsPerPage ? (
                <div className={styles.customerPagination}>
                    <UI.Pagination
                        onPageChanged={onPageChanged}
                        numberOfDataEntries={props.totalEntries}
                        dataEntriesPerPage={props.itemsPerPage}
                        initialPage={props.currentPage}
                    />
                </div>
            ) : null}
        </>
    );
};

export const normalizeProgress = (progress?: number | null, status?: 1 | 2 | 3 | null) => {
    const maxProgress = status === 3 ? FAILED_COPY_PROGRESS_LIMIT : COMPLETED_COPY_PROGRESS_LIMIT;
    return Math.max(0, Math.min(progress ?? 0, maxProgress));
};

export default observer(CopiedCustomerList);
